import PropTypes from "prop-types";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { formatNotificationDate } from "./format-notification-date";
import {
  Box,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemText,
  Popover,
  Stack,
  SvgIcon,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import useNotificationsStore from "@stores/shared/useNotificationsStore";
import { Scrollbar } from "@components/admin/elements/scrollbar";
import { CopyLinkButton } from "@components/shared/elements/copy-link-button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleExclamation } from "@fortawesome/pro-regular-svg-icons";
import { faThumbTack } from "@fortawesome/pro-solid-svg-icons";
import EmptyState from "@components/shared/elements/empty-state";

// Shared reset for "Read more"/"Remove" — both render as native <button>
// elements (not <a>/plain Link) so they share identical box-model/typography
// behavior and stay pixel-aligned with each other, rather than fighting the
// browser's differing default button vs. anchor styling.
const LINK_BUTTON_SX = {
  p: 0,
  m: 0,
  border: 0,
  background: "none",
  fontFamily: "inherit",
  verticalAlign: "baseline",
  fontWeight: 600,
  cursor: "pointer",
};

// Small on/off dot toggle for seen/unseen — filled when unseen, hollow when
// seen. Sits in the row's secondaryAction slot (top-right), where the delete
// control used to live; delete moved down into the content area as a text
// link next to "Read more".
const UnreadDot = ({ seen, disabled, onToggle, t }) => (
  <Tooltip title={disabled ? t("notifications.acknowledgeFirst") : t(seen ? "common.markUnseen" : "common.markSeen")}>
    <span>
      <IconButton
        aria-label={t(seen ? "common.markUnseen" : "common.markSeen")}
        disabled={disabled}
        onClick={(event) => {
          event.stopPropagation();
          onToggle();
        }}
        size="small"
        sx={{ p: 0.5 }}
      >
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            backgroundColor: seen ? "transparent" : "primary.main",
            border: (theme) =>
              seen ? `1px solid ${theme.palette.divider}` : "none",
          }}
        />
      </IconButton>
    </span>
  </Tooltip>
);

UnreadDot.propTypes = {
  seen: PropTypes.bool,
  disabled: PropTypes.bool,
  onToggle: PropTypes.func,
  t: PropTypes.func,
};

const renderContent = (notification, t, onOpenLink, blocked, onDelete) => {
  const publicationDate = formatNotificationDate(notification.publicationDate);
  return (
      <ListItemText
        primary={
          <Box
            sx={{
              alignItems: "center",
              display: "flex",
              flexWrap: "wrap",
              m: 0,
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                {notification.sticky && (
                  <Tooltip title={t("notifications.sticky")}>
                    <SvgIcon
                      sx={{
                        fontSize: "13px",
                        color: "brand2Text",
                        mr: 0.5,
                        transform: "rotate(45deg)",
                      }}
                    >
                      <FontAwesomeIcon icon={faThumbTack} />
                    </SvgIcon>
                  </Tooltip>
                )}
                <Typography
                  sx={{ mr: 0.5, fontWeight: notification.seen ? 400 : 700 }}
                  variant="subtitle2"
                >
                  {notification.title}
                </Typography>
                {notification.requireAcknowledgement && !notification.acknowledged && (
                  <Tooltip title={t("notifications.alertTooltip")}>
                    <SvgIcon
                      fontSize="small"
                      sx={{
                        color: "error.main",
                        mr: 0.5,
                      }}
                    >
                      <FontAwesomeIcon icon={faCircleExclamation} />
                    </SvgIcon>
                  </Tooltip>
                )}
              </Box>
              {publicationDate && (
                <Typography color="textSecondary" variant="caption">
                  {publicationDate}
                </Typography>
              )}
            </Box>
          </Box>
        }
        secondary={
          <Box>
            <Typography variant="body2">{notification.content}</Typography>
            {/* Text actions on the left; the copy icon at the row's end, in the
                column of the unread dot (the row's icon controls). A flex gap,
                so the icon's auto margin isn't reset by Stack spacing. */}
            <Stack direction="row" spacing={1.5} useFlexGap sx={{ mt: 0.5, alignItems: "center" }}>
              {notification.link && (
                <Link
                  component="button"
                  type="button"
                  variant="caption"
                  underline="hover"
                  onClick={(event) => {
                    event.stopPropagation();
                    onOpenLink?.();
                    window.open(notification.link, "_blank", "noopener,noreferrer");
                  }}
                  sx={{ ...LINK_BUTTON_SX, color: "brandText" }}
                >
                  {t("notifications.readMore")}
                </Link>
              )}
              <Tooltip title={blocked ? t("notifications.acknowledgeFirst") : ""}>
                <span>
                  <Link
                    component="button"
                    type="button"
                    variant="caption"
                    underline="hover"
                    disabled={blocked}
                    onClick={(event) => {
                      event.stopPropagation();
                      onDelete();
                    }}
                    sx={{
                      ...LINK_BUTTON_SX,
                      color: blocked ? "text.disabled" : "brandText",
                      cursor: blocked ? "default" : "pointer",
                    }}
                  >
                    {t("common.remove")}
                  </Link>
                </span>
              </Tooltip>
              {/* Copying counts as opening: acknowledge like "Read more" so
                  the link can't be taken elsewhere without acknowledging. */}
              {notification.link && (
                <CopyLinkButton
                  url={notification.link}
                  color="inherit"
                  iconSize={12}
                  onCopy={() => onOpenLink?.()}
                  sx={{ p: 0.25, ml: "auto", color: "brandText" }}
                />
              )}
            </Stack>
          </Box>
        }
        primaryTypographyProps={{ component: "div" }}
        secondaryTypographyProps={{ component: "div" }}
        sx={{ my: 0 }}
      />
  );
};

export const NotificationsPopover = (props) => {
  const { anchorEl, onClose, open = false, ...other } = props;

  const { t } = useTranslation();
  const { notifications, toggleSeen, acknowledge, deleteOne, fetchItems } =
    useNotificationsStore();

  const successCallback = () => {
    fetchItems();
  };

  const deleteSuccessCallback = () => {
    toast.success(t("notifications.deleted"));
    fetchItems();
  };

  const errorCallback = () => {
    toast.error(t("error.wrong"));
  };

  const isEmpty = !notifications || notifications.length === 0;
  const unseenCount = notifications?.filter((n) => !n.seen).length ?? 0;

  const isBlocked = (notification) =>
    Boolean(notification.requireAcknowledgement) && !notification.acknowledged;

  const handleOpenNotification = (notification) => {
    if (isBlocked(notification)) {
      acknowledge(notification.id, undefined, errorCallback);
    }
  };

  return (
    <Popover
      data-testid="notifications-popover"
      anchorEl={anchorEl}
      anchorOrigin={{
        horizontal: "left",
        vertical: "bottom",
      }}
      disableScrollLock
      disableAutoFocus
      disableEnforceFocus
      onClose={onClose}
      open={open}
      slotProps={{
        paper: { sx: { width: 380 } },
      }}
      {...other}
    >
      <Stack
        direction="row"
        sx={{
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 2
        }}>
        <Typography variant="h6" sx={{
          color: "inherit"
        }}>
          {t("common.notifications")}
        </Typography>
        {!isEmpty && (
          <Typography variant="caption" color="textSecondary">
            {t("notifications.unseenSummary", {
              unseen: unseenCount,
              total: notifications.length,
            })}
          </Typography>
        )}
      </Stack>
      {isEmpty ? (
        <EmptyState size="small" title={t("common.noNotification")} />
      ) : (
        <Scrollbar sx={{ maxHeight: 400 }}>
          <List disablePadding sx={{ pb: 1 }}>
            {notifications.map((notification) => {
              const blocked = isBlocked(notification);
              return (
                <ListItem
                  divider
                  key={notification.id}
                  sx={{
                    paddingLeft: 2,
                    paddingRight: 2.5,
                    alignItems: "flex-start",
                    backgroundColor: notification.seen
                      ? "transparent"
                      : (theme) => alpha(theme.palette.primary.main, 0.04),
                    "&:hover": {
                      backgroundColor: "action.hover",
                    },
                    "& .MuiListItemSecondaryAction-root": {
                      top: "24%",
                    },
                  }}
                  secondaryAction={
                    <UnreadDot
                      seen={notification.seen}
                      disabled={blocked}
                      onToggle={() =>
                        toggleSeen(notification.id, successCallback, errorCallback)
                      }
                      t={t}
                    />
                  }
                >
                  {renderContent(
                    notification,
                    t,
                    () => handleOpenNotification(notification),
                    blocked,
                    () =>
                      deleteOne(
                        notification.id,
                        deleteSuccessCallback,
                        errorCallback
                      )
                  )}
                </ListItem>
              );
            })}
          </List>
        </Scrollbar>
      )}
    </Popover>
  );
};

NotificationsPopover.propTypes = {
  anchorEl: PropTypes.any,
  onClose: PropTypes.func,
  open: PropTypes.bool,
};
